import React, { useState, useEffect } from 'react';
import desktopScreenshot from '../../img/lhscreenshot2.png';
import heroBg from '../../img/lhcover.png';
import lhtitle from '../../img/lonelyhandslogo_nobg.png'
import ReactGA from "react-ga4";

export default function LonelyHandsPage() {
  // --- RESPONSIVE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- THEMED PARODY STYLES ---
  const styles = {
    page: {
      backgroundColor: '#f0f2f5', // Soft, clean platform background grey
      color: '#334155', // Deep slate for highly readable text
      fontFamily: '"Inter", sans-serif',
      minHeight: '100vh',
    },
    heroSection: {
      width: '100%',
      height: isMobile ? '45vh' : '55vh',
      // Bright linear gradient fading into a crisp white background to contrast the logo perfectly
      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 1)), url(${heroBg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: '0 20px',
      borderBottom: '1px solid #e2e8f0', 
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    },
    logoImage: {
      height: isMobile ? '60px' : '110px', // Restricts the image height so it stays crisp and perfectly proportioned
      width: 'auto',
      marginBottom: '15px',
    },
    heroSubtitle: {
      fontSize: isMobile ? '1.1rem' : '1.4rem',
      fontWeight: '500',
      color: '#64748b', // Subdued interface grey
      maxWidth: '600px',
      marginBottom: '30px',
    },
    badgeContainer: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    badgePrimary: {
      backgroundColor: '#00AFF0', // The classic parody cyan
      color: '#ffffff',
      padding: '6px 14px',
      borderRadius: '20px', // Rounded "pill" badges to match modern app UIs
      fontWeight: '600',
      fontSize: isMobile ? '0.75rem' : '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    content: {
      maxWidth: '1000px',
      margin: '-30px auto 0', 
      padding: isMobile ? '40px 20px' : '50px',
      backgroundColor: '#ffffff', // Clean crisp white content card
      borderRadius: '16px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.06)', 
      position: 'relative',
      zIndex: 10,
      border: '1px solid #e2e8f0',
      width: isMobile ? '90%' : 'auto',
    },
    sectionTitle: {
      fontSize: isMobile ? '1.6rem' : '2.2rem',
      fontWeight: '800',
      marginBottom: '20px',
      color: '#0f172a',
      textAlign: 'center',
    },
    paragraph: {
      fontSize: '1.1rem',
      lineHeight: '1.8',
      color: '#475569',
      marginBottom: '20px',
      textAlign: isMobile ? 'left' : 'center',
    },
    screenshotContainer: {
      display: 'flex',
      justifyContent: 'center',
      margin: '40px 0',
    },
    screenshot: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '12px', 
      boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
      border: '1px solid #e2e8f0',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '30px',
      marginTop: '40px',
    },
    featureCard: {
      padding: isMobile ? '20px' : '30px',
      backgroundColor: '#f8fafc', // Light feed item background
      borderRadius: '12px',
      borderTop: '4px solid #00AFF0', // Visual accent matching the logo script
      boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
      borderLeft: '1px solid #e2e8f0',
      borderRight: '1px solid #e2e8f0',
      borderBottom: '1px solid #e2e8f0',
    },
    featureTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      marginBottom: '12px',
      color: '#0f172a',
    },
    buttonGroup: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'center',
      gap: '20px',
      marginTop: '50px',
    },
    primaryButton: {
      padding: '16px 40px',
      backgroundColor: '#00AFF0', 
      color: '#ffffff',
      fontWeight: '700',
      border: 'none',
      fontSize: '1.1rem',
      borderRadius: '30px', // Smooth pill shape button
      cursor: 'pointer',
      textDecoration: 'none',
      transition: 'background 0.2s, transform 0.1s',
      textAlign: 'center',
      boxShadow: '0 4px 14px rgba(0, 175, 240, 0.3)',
    },
    secondaryButton: {
      padding: '16px 40px',
      backgroundColor: '#ffffff',
      color: '#00AFF0',
      fontWeight: '700',
      border: '2px solid #00AFF0',
      fontSize: '1.1rem',
      borderRadius: '30px',
      cursor: 'pointer',
      textDecoration: 'none',
      textAlign: 'center',
      transition: 'background 0.2s',
    }
  };

  return (
    <div style={styles.page}>
      
      {/* HERO SECTION */}
      <div style={styles.heroSection}>
        {/* NEW TRANSPARENT LOGO REPLACEMENT */}
        <img src={lhtitle} alt="LonelyHands Logo" style={styles.logoImage} />
        
        <p style={styles.heroSubtitle}>
          A pixel-art desktop simulator about the business of digital intimacy.
        </p>
        
        {/* THE BADGE SYSTEM */}
        <div style={styles.badgeContainer}>
          <span style={styles.badgePrimary}>Status: Alpha</span>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={styles.content}>
        
        <h2 style={styles.sectionTitle}>Totally Not an OF Simulator</h2>
        <p style={styles.paragraph}>
          You are a struggling college student drowning in tuition debt. Desperate for cash, you stumble into a highly lucrative, morally grey gig: working as a "ghostwriter" for top models on a massive subscription-based adult platform.
        </p>
        <p style={styles.paragraph}>
          The clients think they are talking to their favorite creators. In reality, they are talking to you. 
          Your job is to manage the chat queues, string them along, and maximize tips without crossing your own personal lines.
        </p>

        {/* DESKTOP UI SHOWCASE */}
        <div style={styles.screenshotContainer}>
          <img src={desktopScreenshot} alt="Simulated Desktop UI" style={styles.screenshot} />
        </div>

        {/* FEATURES GRID */}
        <div style={styles.grid}>
          
          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Desktop OS</h3>
            <p style={{color: '#64748b', lineHeight: '1.6'}}>
              The core loop takes place on a simulated computer desktop rendered in minimalist pixel art. Juggle multiple clients as you build the perfect response for each of them, balancing sweetness, teasing, desperation, etc... for maximum tips!
            </p>
          </div>

          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Moral Grey Area</h3>
            <p style={{color: '#64748b', lineHeight: '1.6'}}>
              Explore the dark side of parasocial relationships. When your own survival is on the line, how far will you manipulate a stranger on the other side of the screen? With rent coming up and your roommate out of a job, it's up to you to keep you both afloat.
            </p>
          </div>

          <div style={styles.featureCard}>
            <h3 style={styles.featureTitle}>Manage Models</h3>
            <p style={{color: '#64748b', lineHeight: '1.6'}}>
              Manage up to 4 different models' chats at the same time! Breaking character or sending the wrong message can be disastrous, so stay focused as your salary depends on it.
            </p>
          </div>

        </div>

        {/* TEASER BUTTONS */}
        <div style={styles.buttonGroup}>
          <a 
            href="https://x.com/ForeplayGames" 
            target="_blank"
            rel="noopener noreferrer"
            style={styles.primaryButton}
            onClick={() => {
              ReactGA.event({
                category: "Outbound Link",
                action: "Click_Twitter",
                label: "LonelyHands Page"
              });
            }}
          >
            Follow on Twitter
          </a>
          <a 
            href="https://foreplaygames.itch.io/lonelyhands" 
            style={styles.secondaryButton}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              ReactGA.event({
                category: "Outbound Link",
                action: "Click_Itch",
                label: "LonelyHands Page"
              });
            }}
          >
            Follow on Itch.io
          </a>
        </div>

      </div>
    </div>
  );
}